import { useTranslation } from 'react-i18next';
import { useBooleanFlagValue } from '@openfeature/react-sdk';
import { Building2Icon, LayoutGridIcon, UserIcon, type LucideIcon } from 'lucide-react';
import {
  Badge,
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@monksflow/monks-ui';

/** Who a custom agent built in the agent builder is shared with. */
export type AgentVisibility = 'org' | 'workspace' | 'user';

/** The glyph for each scope, the same one the agent builder's scope picker shows. */
export const AGENT_VISIBILITY_ICONS: Record<AgentVisibility, LucideIcon> = {
  org: Building2Icon,
  workspace: LayoutGridIcon,
  user: UserIcon,
};

/**
 * Marks an agent someone built in the agent builder, and says who it is shared with: on hover, and
 * to a screen reader as part of the chip. Draws nothing where the agent builder is off.
 */
export function CustomAgentChip({ visibility }: { visibility: AgentVisibility }) {
  const { t } = useTranslation('common');
  const builderOn = useBooleanFlagValue('relay_agent_builder', false);
  if (!builderOn) return null;
  const Icon = AGENT_VISIBILITY_ICONS[visibility];
  const explanation = t(`components.customAgentChip.${visibility}`);
  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger asChild>
          <Badge variant="outline" data-testid="custom-agent-chip" className="shrink-0 gap-2xs">
            <Icon aria-hidden className="size-3" />
            <span aria-hidden>{t('components.customAgentChip.label')}</span>
            <span className="sr-only">{explanation}</span>
          </Badge>
        </TooltipTrigger>
        <TooltipContent>{explanation}</TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}
